import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import AppShell from '../components/AppShell';
import { SummarosMark } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// After every N questions, show a check-in screen: "Enough for now,
// or keep going?" Users can stop early and still build a profile
// with whatever they've answered.
const CHECKIN_EVERY = 8;

/**
 * Life Assessment.
 *
 * The question deck is interleaved BY PASS across domains:
 *   Pass 1 asks one question of each of the 12 domains, then
 *   Pass 2 does the same, then Pass 3.
 * That way the first 12 questions cover the whole picture at a
 * shallow level; the next 12 add friction/problem detail; the last
 * 12 surface secret ups and beliefs. It also kills the repetitive
 * "still on the same domain" feeling.
 */
const Assess = () => {
  const [schema, setSchema] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [showCheckin, setShowCheckin] = useState(false); // check-in overlay
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const navigate = useNavigate();
  const draftDebounce = useRef(null);

  // Flat list, interleaved by pass. [(domain, question)]
  const flat = useMemo(() => {
    if (!schema) return [];
    // Group questions of each domain by pass, in domain declaration order
    const passes = {};   // pass_no -> [(domain, question)]
    for (const d of schema.domains) {
      for (const q of d.questions) {
        const p = q.pass ?? 1;
        if (!passes[p]) passes[p] = [];
        passes[p].push({ domain: d, question: q });
      }
    }
    // Concatenate passes in ascending order
    const out = [];
    for (const p of Object.keys(passes).sort((a, b) => Number(a) - Number(b))) {
      out.push(...passes[p]);
    }
    return out;
  }, [schema]);

  // Load schema + draft in parallel; resume at first unanswered.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [schemaRes, draftRes] = await Promise.all([
          axios.get(`${API}/assessment/schema`),
          axios.get(`${API}/assessment/draft`).catch(() => ({ data: { answers: {} } })),
        ]);
        if (cancelled) return;
        setSchema(schemaRes.data);
        const answered = draftRes.data?.answers || {};
        setAnswers(answered);
        // Resume at first unanswered (mirroring the interleaved order)
        const passes = {};
        for (const d of schemaRes.data.domains) {
          for (const q of d.questions) {
            const p = q.pass ?? 1;
            if (!passes[p]) passes[p] = [];
            passes[p].push(q.id);
          }
        }
        const flatQids = Object.keys(passes)
          .sort((a, b) => Number(a) - Number(b))
          .flatMap((p) => passes[p]);
        const firstUnanswered = flatQids.findIndex((qid) => !answered[qid]);
        setIdx(firstUnanswered === -1 ? flatQids.length - 1 : firstUnanswered);
      } catch (e) {
        console.error(e);
        toast.error('Could not load the assessment.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Debounced draft save.
  useEffect(() => {
    if (loading || !schema) return;
    if (draftDebounce.current) clearTimeout(draftDebounce.current);
    draftDebounce.current = setTimeout(async () => {
      try {
        setSavingDraft(true); setSaveError(false);
        await axios.put(`${API}/assessment/draft`, { answers });
      } catch { setSaveError(true); } finally { setSavingDraft(false); }
    }, 400);
    return () => draftDebounce.current && clearTimeout(draftDebounce.current);
  }, [answers, loading, schema]);

  const doSubmit = async () => {
    if (Object.keys(answers).length === 0) {
      toast.error('Answer at least one question.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/assessment/submit`, { answers });
      navigate('/life-profile', {
        state: { profile: r.data.profile, assessment_id: r.data.assessment_id },
        replace: true,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save your assessment.');
      setSubmitting(false);
      setShowCheckin(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto px-6 py-32 text-center">
          <Loader2 className="w-6 h-6 text-forest animate-spin mx-auto" strokeWidth={1.5} />
        </div>
      </AppShell>
    );
  }
  if (!schema) return null;

  const total = flat.length;
  const current = flat[idx];
  const progress = Math.round((idx / total) * 100);
  const chosen = answers[current.question.id] || null;
  const isLast = idx === total - 1;
  const isFirst = idx === 0;

  // Count answered questions so the check-in message is accurate
  // regardless of skips.
  const answeredCount = Object.keys(answers).length;

  const advance = () => {
    if (isLast) return;
    // If we just answered a multiple-of-N question, show the check-in
    // instead of silently moving on. Use the position in the deck,
    // not the raw answered count, so skipped questions still trigger
    // the interstitial at the intended cadence.
    const nextPos = idx + 1; // one-based number of questions attempted
    if (nextPos > 0 && nextPos % CHECKIN_EVERY === 0 && nextPos < total) {
      setShowCheckin(true);
      return;
    }
    setIdx((i) => Math.min(i + 1, total - 1));
  };

  const pick = (optionId) => {
    setAnswers((prev) => ({ ...prev, [current.question.id]: optionId }));
    setTimeout(() => { advance(); }, 220);
  };

  const goPrev = () => setIdx((i) => Math.max(0, i - 1));
  const goNextSkip = () => advance();

  return (
    <AppShell hideFooter>
      <SEO title="Life Assessment" description="A short, conversational look across the parts of your life." path="/assess" noindex />

      {/* ── Progress bar ─────────────────────────────── */}
      <div className="sticky top-[68px] z-20 bg-cream/95 backdrop-blur border-b border-line">
        <div className="max-w-2xl mx-auto px-6 pt-3 pb-4">
          <div className="flex items-center justify-between text-[12px] text-ink-muted mb-2 font-mono">
            <span data-testid="progress-domain">{current.domain.eyebrow.toUpperCase()}</span>
            <span data-testid="progress-count">{idx + 1} / {total}</span>
          </div>
          <div className="h-1 bg-cream-deep rounded-full overflow-hidden">
            <div
              className="h-full bg-forest transition-all duration-500"
              style={{ width: `${progress}%` }}
              data-testid="progress-bar"
            />
          </div>
          <div className="flex items-center justify-between mt-2 text-[11px] text-ink-faint">
            <button
              onClick={() => navigate('/my-life')}
              className="hover:text-forest transition-colors"
              data-testid="assess-leave"
            >
              Leave and come back later
            </button>
            <span data-testid="assess-save-state">
              {savingDraft && <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} />saving…</span>}
              {!savingDraft && !saveError && <span className="inline-flex items-center gap-1"><Check className="w-3 h-3" strokeWidth={2} />saved</span>}
              {saveError && <span className="text-clay">not saved</span>}
            </span>
          </div>
        </div>
      </div>

      {/* ── Question ───────────────────────────────── */}
      <section className="max-w-2xl mx-auto px-6 pt-12 pb-24" data-testid="assess-question">
        <p className="eyebrow mb-6 animate-fade-in">{current.domain.title}</p>
        <h1
          key={current.question.id}
          className="display-serif text-3xl sm:text-[42px] leading-[1.15] text-forest text-balance animate-fade-up"
          style={{ fontVariationSettings: "'opsz' 72, 'SOFT' 40, 'wght' 380" }}
          data-testid="assess-prompt"
        >
          {current.question.prompt}
        </h1>

        <div className="mt-10 grid gap-3" data-testid="assess-options">
          {current.question.options.map((opt) => {
            const isChosen = chosen === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => pick(opt.id)}
                className={`text-left px-5 py-4 rounded-xl border transition-all duration-200
                  ${isChosen
                    ? 'border-forest bg-forest text-cream shadow-sm'
                    : 'border-line bg-cream-soft hover:border-forest hover:bg-cream-deep text-ink'
                  }`}
                data-testid={`assess-option-${opt.id}`}
              >
                <span className="flex items-center gap-3">
                  <span
                    className={`inline-flex items-center justify-center w-6 h-6 rounded-full border-2 transition-colors shrink-0
                      ${isChosen ? 'border-cream bg-cream text-forest' : 'border-line-strong bg-transparent'}`}
                  >
                    {isChosen && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                  </span>
                  <span className="text-[16px] leading-snug">{opt.label}</span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-10 flex items-center justify-between">
          <button
            onClick={goPrev}
            disabled={isFirst}
            className="text-[14px] text-ink-muted hover:text-forest disabled:opacity-30 disabled:pointer-events-none inline-flex items-center gap-1.5 transition-colors"
            data-testid="assess-prev"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Back
          </button>

          {!isLast && (
            <button
              onClick={goNextSkip}
              className="text-[14px] text-ink-muted hover:text-forest inline-flex items-center gap-1.5 transition-colors"
              data-testid="assess-next"
            >
              Skip <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          )}

          {isLast && (
            <button
              onClick={doSubmit}
              disabled={submitting}
              className="btn-primary"
              data-testid="assess-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                  Building your profile…
                </>
              ) : (
                <>
                  Reveal my Life Profile
                  <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </>
              )}
            </button>
          )}
        </div>
      </section>

      <div className="pb-16 flex items-center justify-center gap-2 text-[12px] text-ink-faint">
        <SummarosMark size={16} /> Summaros
      </div>

      {/* ── Check-in modal ──────────────────────────── */}
      {showCheckin && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-forest/40 backdrop-blur-sm animate-fade-in"
          data-testid="assess-checkin"
        >
          <div className="relative w-full max-w-md bg-cream border border-line-strong rounded-2xl p-7 sm:p-9 shadow-lg animate-fade-up">
            <p className="eyebrow mb-4">A quick check-in</p>
            <h2
              className="display-serif text-2xl sm:text-3xl text-forest mb-3"
              style={{ fontVariationSettings: "'opsz' 64, 'SOFT' 40, 'wght' 400" }}
              data-testid="assess-checkin-title"
            >
              Enough for now, or keep going?
            </h2>
            <p className="text-[15px] leading-[1.6] text-ink-muted mb-7">
              You've answered <span className="text-forest font-medium">{answeredCount}</span> of {total}.
              We can stop here and build your Life Profile with what you've told us. Or you can keep going for a fuller picture.
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={doSubmit}
                disabled={submitting}
                className="btn-secondary"
                data-testid="assess-checkin-stop"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Building…</>
                ) : (
                  <>Stop here and build my profile</>
                )}
              </button>
              <button
                onClick={() => {
                  setShowCheckin(false);
                  setIdx((i) => Math.min(i + 1, total - 1));
                }}
                className="btn-primary"
                data-testid="assess-checkin-continue"
              >
                Keep going <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
};

export default Assess;
